import { useEffect, useState } from "react";
import { Ban, KeyRound, Trash2, CheckCircle2, Lock, Crown, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AdminTitle } from "@/admin/AdminLayout";

const Act = ({ icon: Icon, label, onClick, testid, danger }) => (
  <button onClick={onClick} data-testid={testid} title={label} className={`p-2 ${danger ? "text-[#991b1b] hover:bg-[#991b1b]/10" : "hover:bg-[#c9a227]/20"}`}><Icon className="w-4 h-4" /></button>
);

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const reset = (u) => { const pw = window.prompt(`New password for ${u.email} (min 8 characters)`); if (pw) act(() => api.post(`/admin/users/${u.id}/password`, { password: pw }), "Password reset — share it with the reader privately"); };
  const remove = (u) => window.confirm(`Delete ${u.email}? Their unpublished drafts are deleted; approved articles stay.`) && act(() => api.delete(`/admin/users/${u.id}`), "Account deleted");
  const list = (users || []).filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-users">
      <AdminTitle eyebrow="The register of the scriptorium" title="Readers & Accounts" />
      <p className="mb-6 italic text-[#4a3a2a] max-w-3xl">Every account is stored in the site's database. Passwords are saved only in scrambled (hashed) form, so nobody, including you, can read them. If a reader forgets theirs, set a new one with the key icon.</p>
      <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="admin-input !w-72 mb-5" />
      <div className="vellum overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="font-heading text-[11px] uppercase tracking-[0.16em] text-[#5e4a35] border-b border-[#c9a227]/50">
            <tr><th className="p-4">Name</th><th className="p-4">Email (login ID)</th><th className="p-4">Role</th><th className="p-4">Joined</th><th className="p-4">Articles</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-b border-[#c9a227]/20">
                <td className="p-4 font-heading font-semibold">{u.name || "—"}</td>
                <td className="p-4">{u.email}</td>
                <td className="p-4">{u.role === "admin" ? <span className="text-[#991b1b] font-bold inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Admin</span>
                  : u.is_master ? <span data-testid={`user-master-${u.email}`} className="text-[#1e4976] font-bold inline-flex items-center gap-1"><Crown className="w-3 h-3" /> Master reader</span> : "Reader"}</td>
                <td className="p-4 text-[#5e4a35]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-4">{u.articles} <span className="text-[#5e4a35]">({u.published} published)</span></td>
                <td className="p-4">{u.disabled ? <span className="text-[#991b1b] font-bold">Suspended</span> : <span className="text-[#2f6b3a]">Active</span>}</td>
                <td className="p-4">
                  {u.role !== "admin" && (
                    <div className="flex justify-end gap-1">
                      {u.profile_slug && <a href={`/reader/${u.profile_slug}`} target="_blank" rel="noopener noreferrer" data-testid={`user-profile-${u.email}`} title="View profile" className="p-2 hover:bg-[#c9a227]/20"><UserRound className="w-4 h-4" /></a>}
                      {!u.is_master && (u.disabled
                        ? <Act icon={CheckCircle2} label="Reactivate" testid={`user-enable-${u.email}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/disable`, { disabled: false }), "Reactivated")} />
                        : <Act icon={Ban} label="Suspend" testid={`user-suspend-${u.email}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/disable`, { disabled: true }), "Suspended")} />)}
                      <Act icon={KeyRound} label="Reset password" testid={`user-reset-${u.email}`} onClick={() => reset(u)} />
                      {!u.is_master && <Act icon={Trash2} label="Delete" danger testid={`user-delete-${u.email}`} onClick={() => remove(u)} />}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users && list.length === 0 && <p className="p-8 text-center italic">No accounts found.</p>}
      </div>
    </div>
  );
}
